import api from "./client";

export interface LoginPayload {
  email: string;
  password: string;
}
export interface RegisterPayload {
  name: string;
  email: string;
  password: string;
  companyName: string;
  companySlug: string;
}

export const authApi = {
  login: (data: LoginPayload) => api.post("/auth/login", data),
  register: (data: RegisterPayload) => api.post("/auth/register", data),
  refresh: (refreshToken: string) =>
    api.post("/auth/refresh", { refreshToken }),
  logout: (refreshToken: string) => api.post("/auth/logout", { refreshToken }),
  me: () => api.get("/auth/me"),
  githubAuth: () => {
    window.location.href = "/api/v1/auth/github";
  },
};

export const usersApi = {
  list: (params?: Record<string, unknown>) => api.get("/users", { params }),
  invite: (data: unknown) => api.post("/users/invite", data),
  getOne: (id: string) => api.get(`/users/${id}`),
  updateRole: (id: string, role: string) =>
    api.patch(`/users/${id}/role`, { role }),
  deactivate: (id: string) => api.delete(`/users/${id}`),
};

export const companyApi = {
  get: () => api.get("/company"),
  update: (data: unknown) => api.patch("/company", data),
  stats: () => api.get("/company/stats"),
  listApiKeys: () => api.get("/company/api-keys"),
  createApiKey: (name: string) => api.post("/company/api-keys", { name }),
  revokeApiKey: (id: string) => api.delete(`/company/api-keys/${id}`),
};

export const revenueApi = {
  // Optional `signal` lets callers pass an AbortController.signal so that
  // in-flight requests are cancelled when a newer query supersedes them.
  list: (params?: Record<string, unknown>, signal?: AbortSignal) =>
    api.get("/revenue", { params, signal }),
  get: (id: string) => api.get(`/revenue/${id}`),
  create: (data: unknown) => api.post("/revenue", data),
  update: (id: string, data: unknown) => api.patch(`/revenue/${id}`, data),
  delete: (id: string) => api.delete(`/revenue/${id}`),
  analytics: (params?: Record<string, unknown>, signal?: AbortSignal) =>
    api.get("/revenue/analytics", { params, signal }),
  listLeakages: (params?: Record<string, unknown>, signal?: AbortSignal) =>
    api.get("/revenue/leakages", { params, signal }),
  createLeakage: (data: unknown) => api.post("/revenue/leakages", data),
  resolveLeakage: (id: string) => api.post(`/revenue/leakages/${id}/resolve`),
  deleteLeakage: (id: string) => api.delete(`/revenue/leakages/${id}`),
};

export const billingApi = {
  getSubscription: () => api.get("/billing/subscription"),
  getInvoices: () => api.get("/billing/invoices"),
  createCheckout: (plan: string) => api.post("/billing/checkout", { plan }),
  createPortal: () => api.post("/billing/portal"),
};

export const integrationsApi = {
  githubStatus: () => api.get("/integrations/github/status"),
  githubDisconnect: () => api.delete("/integrations/github"),
  githubRepos: () => api.get("/integrations/github/repos"),

  // ── Enterprise Integration Endpoints ────────────────────────────────────
  /** List all connected integrations with health status */
  list: () => api.get("/integrations"),
  /** Get integration detail by ID */
  getOne: (id: string) => api.get(`/integrations/${id}`),
  /** Connect a new provider */
  connect: (data: {
    provider: string;
    accessToken?: string;
    refreshToken?: string;
    apiKey?: string;
    clientSecret?: string;
    scopes?: string;
    permissions?: string[];
    config?: Record<string, unknown>;
  }) => api.post("/integrations/connect", data),
  /** Disconnect provider */
  disconnect: (id: string) => api.post(`/integrations/${id}/disconnect`),
  /** Trigger manual sync — uses a longer timeout because the backend
   *  calls an external API and may take up to 20 s on first run. */
  sync: (id: string) =>
    api.post(`/integrations/${id}/sync`, undefined, { timeout: 25_000 }),
  /** Get integration audit logs */
  logs: (
    id: string,
    params?: { limit?: number; offset?: number; action?: string },
  ) => api.get(`/integrations/${id}/logs`, { params }),
  /** Rotate credentials */
  rotateCredentials: (
    id: string,
    data: {
      accessToken?: string;
      refreshToken?: string;
      apiKey?: string;
      clientSecret?: string;
    },
  ) => api.post(`/integrations/${id}/rotate`, data),
  /** Get health dashboard */
  health: () => api.get("/integrations/health"),
};

export const customProvidersApi = {
  /** Create a new custom provider — POST /integrations/providers/custom */
  create: (payload: unknown) =>
    api.post<{ data: unknown }>("/integrations/providers/custom", payload),
  /** Delete a custom provider — DELETE /integrations/providers/custom/:id */
  remove: (id: string) => api.delete(`/integrations/providers/custom/${id}`),
  /** Optional connectivity ping — POST /integrations/providers/custom/:id/ping */
  ping: (id: string) =>
    api.post<{
      ok: boolean;
      statusCode: number;
      latencyMs?: number;
      error?: string;
    }>(`/integrations/providers/custom/${id}/ping`),
};

// Audit trail — endpoint may not be deployed yet; callers handle 404 gracefully
export const auditApi = {
  list: (params?: Record<string, unknown>, signal?: AbortSignal) =>
    api.get("/audit", { params, signal }),
};

// User preferences (theme etc.) — endpoint may not be deployed yet; callers handle 404 gracefully
export const userPreferencesApi = {
  get: () => api.get<{ data: { theme: string } }>("/users/me/preferences"),
  update: (data: { theme?: string }) =>
    api.patch("/users/me/preferences", data),
};

import type {
  ListInsightsQuery,
  PaginatedInsights,
  AIInsight,
  RunAnalysisBody,
  AIModel,
  AIUsage,
} from "@/types/api";

export const aiApi = {
  /** List AI insights with pagination and optional severity filter. */
  listInsights: (params?: ListInsightsQuery) =>
    api.get<{ data: PaginatedInsights }>("/ai/insights", { params }),

  /** Get a single insight by ID. */
  getInsight: (id: string) =>
    api.get<{ data: AIInsight }>(`/ai/insights/${id}`),

  /** Mark an insight as read. */
  markRead: (id: string) =>
    api.patch<{ data: { id: string; isRead: boolean } }>(
      `/ai/insights/${id}/read`,
    ),

  /**
   * Run a new intelligence analysis.
   * Statistical analysis runs unconditionally; LLM enrichment is opt-in.
   */
  runAnalysis: (body: RunAnalysisBody = {}) =>
    api.post<{ data: unknown }>("/ai/run", body),

  /** List enabled AI models with API-key availability flags. */
  listModels: () => api.get<{ data: AIModel[] }>("/ai/models"),

  /** Get current AI usage quotas and counters. */
  getUsage: () => api.get<{ data: AIUsage }>("/ai/usage"),
};
